import packageJson from "../../../package.json";
import { getLocalStorage } from "../utils/localStorage";
import { getSessionStorage } from "../utils/sessionStorage";
import { isValidObject } from "../utils";

import { DT_EVENTS, DT_ATTRIBUTES } from "../constants/dynatrace";
import { ANALYTICS_STORAGE_KEYS } from "../constants/analytics";
import storageConstants from "../constants/storage";
import { API_HEADERS } from "../constants/apiHeaders";
import moment from "moment";

const { DEVICE_ID, USER_PROFILE, AUTH_USER_CONTENT } = storageConstants;

let dynatraceTrackingEnabled = false;
let dynatraceApiErrorTrackingEnabled = false;
let dynatraceEventModifierRegistered = false;
const sessionProperties = {};

/** Retrieve essential attributes for Dynatrace events
 * @returns {Object} Object containing essential attributes
 */
function retrieveEssentialAttributes() {
  const userProfile = getLocalStorage(USER_PROFILE);
  const authUserContent = getLocalStorage(AUTH_USER_CONTENT);
  const profileData = userProfile?.profile?.profileData || authUserContent?.data?.resultObj?.profile?.profileData || {};

  return {
    [DT_ATTRIBUTES.APP_VERSION]: packageJson.version,
    [DT_ATTRIBUTES.DEVICE_ID]: getLocalStorage(DEVICE_ID),
    [DT_ATTRIBUTES.TV_ACCOUNT_ID]: profileData?.crmAccountId,
    [DT_ATTRIBUTES.USER_UUID]: profileData?.uuidExternal,
    [DT_ATTRIBUTES.USER_AVS_SESSION_ID]: getSessionStorage(ANALYTICS_STORAGE_KEYS.SESSION_ID),
  };
}

/**
 * Registers a Dynatrace event modifier to enrich all outgoing Dynatrace events.
 */
function initDynatraceEventModifier() {
  if (dynatraceEventModifierRegistered) return;

  // Helper to prefix keys with "event_properties."
  const prefixEventProps = (attrs) => {
    const out = {};
    if (!attrs) return out;
    Object.keys(attrs).forEach((k) => {
      const v = attrs[k];
      if (v !== undefined && v !== null) {
        out[`event_properties.${k}`] = v;
      }
    });
    return out;
  };

  const modifier = (event, context) => {
    const essentialAttributes = retrieveEssentialAttributes();
    const prefixedAppVersion = prefixEventProps({
      [DT_ATTRIBUTES.APP_VERSION]: essentialAttributes[DT_ATTRIBUTES.APP_VERSION],
    });
    try {
      if (context?.type === "request" && (context?.subType === "xmlhttprequest" || context?.subType === "fetch")) {
        const prefixed = prefixEventProps({
          [DT_ATTRIBUTES.APP_VERSION]: essentialAttributes[DT_ATTRIBUTES.APP_VERSION],
          [DT_ATTRIBUTES.DEVICE_ID]: essentialAttributes[DT_ATTRIBUTES.DEVICE_ID],
          [DT_ATTRIBUTES.TV_ACCOUNT_ID]: essentialAttributes[DT_ATTRIBUTES.TV_ACCOUNT_ID],
        });
        return {
          ...event,
          ...prefixed,
        };
      }
      if (context?.type === "exception") {
        const exception = context.exception;
        if (isValidObject(exception)) {
          const request = exception.request;
          const response = exception.response;
          if (isValidObject(request) && isValidObject(response)) {
            const responseHeaders = response.headers;
            const avsResultCode = responseHeaders?.[API_HEADERS.AVS_RESULT_CODE];
            if (response.status >= 400 || avsResultCode === "KO") {
              const avsTransactionId = responseHeaders?.[API_HEADERS.AVS_TRANSACTION_ID];
              const avsErrorDescription = responseHeaders?.[API_HEADERS.AVS_ERROR_DESCRIPTION];
              const prefixed = prefixEventProps({
                [DT_ATTRIBUTES.APP_VERSION]: essentialAttributes[DT_ATTRIBUTES.APP_VERSION],
                [DT_ATTRIBUTES.DEVICE_ID]: essentialAttributes[DT_ATTRIBUTES.DEVICE_ID],
                [DT_ATTRIBUTES.TV_ACCOUNT_ID]: essentialAttributes[DT_ATTRIBUTES.TV_ACCOUNT_ID],
                [DT_ATTRIBUTES.AVS_TRANSACTION_ID]: avsTransactionId,
                [DT_ATTRIBUTES.ERROR_CODE]: avsErrorDescription,
              });
              return {
                ...event,
                ...prefixed,
              };
            }
          }
        }
      }

      return { ...event, ...prefixedAppVersion };
    } catch (_) {
      // Swallow any modifier errors to avoid breaking event pipeline
      return { ...event, ...prefixedAppVersion };
    }
  };

  // If the agent is already loaded
  if (window.dynatrace?.addEventModifier) {
    window.dynatrace.addEventModifier(modifier);
    dynatraceEventModifierRegistered = true;
    return;
  }

  // Fallback: queue until agent bootstrap executes
  window.dynatraceQ = window.dynatraceQ || [];
  window.dynatraceQ.push(function () {
    if (!dynatraceEventModifierRegistered && window.dynatrace?.addEventModifier) {
      window.dynatrace.addEventModifier(modifier);
      dynatraceEventModifierRegistered = true;
    }
  });
}

export function setDynatraceApiErrorTrackingEnabled(value) {
  dynatraceApiErrorTrackingEnabled = !!value;
}

export function setDynatraceTrackingEnabled(value) {
  dynatraceTrackingEnabled = !!value;
  if (dynatraceTrackingEnabled) {
    initDynatraceEventModifier();
  }
}

export function isDynatraceTrackingEnabled() {
  return dynatraceTrackingEnabled;
}

export function isDynatraceApiErrorTrackingEnabled() {
  return dynatraceApiErrorTrackingEnabled;
}

// Helper to safely send business events
export const safeSendBizEvent = (name, payload) => {
  if (!dynatraceTrackingEnabled) return;

  const essentialAttributes = retrieveEssentialAttributes();

  try {
    window.dynatrace?.sendBizEvent?.(name, {
      ...payload,
      ...essentialAttributes,
    });
  } catch (_) {
    // swallow to avoid breaking app if Dynatrace not present
  }
};

/**
 * Parses the given attributes into separate objects based on their types.
 *
 * @param {Object} attributes - The attributes to parse.
 * @param {Object} attributes - The attributes object where keys are attribute names and values are attribute values.
 * @returns {Object} An object containing three properties:
 * - stringAttributes: An object containing all attributes that are not numbers, with their values converted to strings.
 * - intAttributes: An object containing all attributes that are integers.
 * - doubleAttributes: An object containing all attributes that are non-integer numbers.
 */
const parseAttributes = (attributes) => {
  const stringAttributes = {};
  const intAttributes = {};
  const doubleAttributes = {};

  for (const key in attributes) {
    if (typeof attributes[key] === "number") {
      if (Number.isInteger(attributes[key])) {
        intAttributes[key] = attributes[key];
      } else {
        doubleAttributes[key] = attributes[key];
      }
    } else {
      stringAttributes[key] = String(attributes[key]);
    }
  }

  return { stringAttributes, intAttributes, doubleAttributes };
};

/**
 * Wrapper for tracking custom events
 * @param {String} name
 * @param {Object} attributes Custom attributes that are specific to the action
 * @param {Boolean} isDynatraceTrackingEnabled Flag to enable Dynatrace tracking
 */
export const logDTEvent = (name, attributes = {}, isDynatraceTrackingEnabled) => {
  if (!name || !window.dtrum || !isDynatraceTrackingEnabled) {
    return;
  }
  const actionId = window.dtrum.enterAction?.(name);
  if (actionId) {
    const { stringAttributes, intAttributes, doubleAttributes } = parseAttributes({
      ...window.pageViewAttributes,
      ...attributes,
    });
    window.dtrum.addActionProperties?.(actionId, intAttributes, null, stringAttributes, doubleAttributes);
  }
  window.dtrum.leaveAction?.(actionId);

  // send business event to Dynatrace along with essential global attributes
  safeSendBizEvent(name, attributes);
};

// Helper to prefix keys with "session_properties."
const prefixSessionProps = (attrs) => {
  const out = {};
  if (!attrs) return out;
  Object.keys(attrs).forEach((k) => {
    const v = attrs[k];
    if (v !== undefined && v !== null) {
      out[`session_properties.${k}`] = v;
    }
  });
  return out;
};

/**
 * Set a custom attribute that will be added to subsequent events/page actions
 * @param {Object} attributes
 */
export const setDTSessionAttributes = (attributes, isDynatraceTrackingEnabled) => {
  if (!window.dtrum || !attributes || !isDynatraceTrackingEnabled) {
    return;
  }
  const { stringAttributes, intAttributes, doubleAttributes } = parseAttributes(attributes);
  window.dtrum.sendSessionProperties?.(intAttributes, null, stringAttributes, doubleAttributes);

  Object.assign(sessionProperties, attributes);

  // send session properties for rum on grail
  const prefixedAttributes = prefixSessionProps(sessionProperties);
  if (window.dynatrace?.sendSessionPropertyEvent) {
    window.dynatrace?.sendSessionPropertyEvent(prefixedAttributes);
  }

  // retrieve essential global attributes for a signed in user
  const userProfile = getLocalStorage(USER_PROFILE);
  const authUserContent = getLocalStorage(AUTH_USER_CONTENT);

  // send a business event to Dynatrace with the global session attributes
  safeSendBizEvent(DT_EVENTS.SET_SESSION_GLOBAL_ATTRIBUTES, {
    ...sessionProperties,
    [DT_ATTRIBUTES.USER_LOGGED_IN]: userProfile || authUserContent ? true : false,
    [DT_ATTRIBUTES.IS_AUTOMATION]: getLocalStorage("isAutomationBuild"),
  });
};

/**
 * Logs errors to dynatrace
 * @param {Object} errorObj - Error object to log
 * @param {Object} attributes Custom attributes that are specific to the error
 */
export const logDTCrash = (errorObj, isDynatraceTrackingEnabled) => {
  if (!errorObj || !isDynatraceTrackingEnabled) {
    return;
  }
  window.dtrum?.reportError?.(errorObj);
};

/**
 * Sets the user identifier for the current session
 * @param {string} username - crmAccountId or device id
 */
export const setDTUser = (username, isDynatraceTrackingEnabled) => {
  if (!isDynatraceTrackingEnabled) {
    return;
  }
  window.dtrum?.identifyUser?.(username);
};

/**
 * Resets the page view attributes
 */
export const resetDTPageAttributes = () => {
  window.pageViewAttributes = {};
};

/**
 * Sets the current page
 * @param {string} name
 */
export const logDTPageView = (name, isDynatraceTrackingEnabled) => {
  if (!isDynatraceTrackingEnabled) {
    return;
  }
  window.dtrum?.setPage({ name, group: name });
  resetDTPageAttributes();
};

/**
 * Sets page attributes by merging the provided attributes with the existing page view attributes.
 *
 * @param {Object} attributes
 */
export const setDTPageAttributes = (attributes, isDynatraceTrackingEnabled) => {
  if (!attributes || !isDynatraceTrackingEnabled) {
    return;
  }
  window.pageViewAttributes = { ...window.pageViewAttributes, ...attributes };
};

// Logs API errors as business events to Dynatrace
export function logDTApiError(payload) {
  // send business event to Dynatrace along with essential global attributes
  safeSendBizEvent(DT_EVENTS.AVS_API_ERROR, {
    [DT_ATTRIBUTES.HTTP_REQUEST_METHOD]: payload.method,
    [DT_ATTRIBUTES.URL]: payload.url?.slice(0, 500),
    [DT_ATTRIBUTES.HTTP_RESPONSE_STATUS_CODE]: String(payload.status ?? ""),
    [DT_ATTRIBUTES.ERROR_CODE]: payload.avsErrorCode ?? "",
    [DT_ATTRIBUTES.AVS_TRANSACTION_ID]: payload.avsTransactionId ?? "",
    [DT_ATTRIBUTES.ERROR_MESSAGE]: payload.message ?? "",
  });
}

// Logs section_opened event
export const logSectionOpened = (sectionName, appTS = undefined, isDynatraceTrackingEnabled) => {
  let dtPayload = {};
  if (appTS) {
    dtPayload = {
      [DT_ATTRIBUTES.SECTION]: sectionName,
      [DT_ATTRIBUTES.TIME_TO_LOAD]: moment().valueOf() - appTS,
    };
  } else {
    dtPayload = {
      [DT_ATTRIBUTES.SECTION]: sectionName,
    };
  }
  logDTEvent(DT_EVENTS.SECTION_OPENED, dtPayload, isDynatraceTrackingEnabled);
};
